// Stage layout config + pure helpers (extracted from StageTab.js).
// Pure data/functions — module-level so they're allocated once and unit-testable.

export const ENTITY_TYPE_COLORS = {
  country_leader: 'hsl(292, 38%, 22%)',
  ceo: 'hsl(197, 37%, 24%)',
  actor: 'hsl(27, 87%, 67%)',
  athlete: 'hsl(173, 58%, 39%)',
  musician: 'hsl(43, 74%, 52%)',
  influencer: 'hsl(340, 65%, 55%)',
};

export const ENTITY_TYPE_LABELS = {
  country_leader: 'Ruler',
  ceo: 'CEO',
  actor: 'Actor',
  athlete: 'Athlete',
  musician: 'Artist',
  influencer: 'Influencer',
};

// Grid layout — columns by entity type × up to 3 rows. We currently
// have 6 active People categories (athlete / musician / actor / ceo /
// country_leader / influencer) so the stage renders 6 columns evenly
// distributed across the arena. Fictional characters were retired in
// v5.7.23 — adding a category back later just means re-tuning
// STAGE_GRID_COLS without touching layout maths.
export const STAGE_GRID_COLS = 6;
export const STAGE_GRID_ROWS = 3;

// Vertical layout — header at 8%, then rows evenly spaced down to 88%
// (8% → 36% → 60% → 84%, ~24% gap)
export const STAGE_HEADER_TOP = 8;
export const STAGE_ROW_TOPS = [84, 60, 36]; // rowIdx 0 (bottom) → 84%, 1 → 60%, 2 (top) → 36%

export function getGridPosition(colIdx, rowIdx) {
  const colSpan = 100 / STAGE_GRID_COLS;
  const left = colSpan * colIdx + colSpan / 2;
  const top = STAGE_ROW_TOPS[Math.min(rowIdx, STAGE_ROW_TOPS.length - 1)];
  return { left: `${left}%`, top: `${top}%` };
}

export function getHeaderPosition(colIdx) {
  const colSpan = 100 / STAGE_GRID_COLS;
  return {
    left: `${colSpan * colIdx + colSpan / 2}%`,
    top: `${STAGE_HEADER_TOP}%`,
  };
}

// PERF (v5.7.37): module-level constants — were re-allocated on every
// StageTab render before (recreated on every SSE tick × ~1/sec).
export const STAGE_CONFIG = {
  colosseum: {
    bg: '/stage-colosseum.jpg',
    bgPosition: 'center 40%',
    entityCenter: { x: 50, y: 62 },
    entityRadius: { x: 28, y: 18 },
  },
  theater: {
    bg: '/stage-theater.jpg',
    bgPosition: 'center 55%',
    entityCenter: { x: 50, y: 58 },
    entityRadius: { x: 25, y: 16 },
  },
  studio: {
    bg: '/stage-studio.jpg',
    bgPosition: 'center 60%',
    entityCenter: { x: 50, y: 55 },
    entityRadius: { x: 30, y: 20 },
  },
};

export const BOOSTER_ICONS = {
  captain_boost:      { emoji: '👑', label: 'Captain 3x' },
  ruler_booster:      { emoji: '👑', label: 'Ruler 2x' },
  ceo_booster:        { emoji: '🏢', label: 'CEO 2x' },
  actor_booster:      { emoji: '🎬', label: 'Actor 2x' },
  athlete_booster:    { emoji: '⚡', label: 'Athlete 2x' },
  musician_booster:   { emoji: '🎨', label: 'Artist 2x' },
  influencer_booster: { emoji: '📣', label: 'Influencer 2x' },
  wildcard:           { emoji: '🃏', label: 'Wildcard' },
  stage_boost:        { emoji: '🔥', label: 'Stage 2x' },
  bench_boost:        { emoji: '🔥', label: 'Stage 2x' },
};

// 2026-09-07 (owner request) — which arena columns a live booster multiplies,
// so the category header can wear a "2x" badge (own Stage + rival rosters).
const BOOSTER_CATEGORY = {
  ruler_booster: 'country_leader', ceo_booster: 'ceo', actor_booster: 'actor',
  athlete_booster: 'athlete', musician_booster: 'musician', influencer_booster: 'influencer',
};
export function boostedCategories(activeBoosters) {
  const out = new Set();
  for (const b of activeBoosters || []) {
    if (b === 'stage_boost' || b === 'bench_boost') CATEGORY_ORDER.forEach((c) => out.add(c));
    else if (BOOSTER_CATEGORY[b]) out.add(BOOSTER_CATEGORY[b]);
  }
  return out;
}

export const CATEGORY_ORDER = ['country_leader', 'ceo', 'actor', 'athlete', 'musician', 'influencer'];

// ─── Shared arena layout builders (2026-07-21, extracted from StageTab) ─────
// Used by BOTH the owner's Stage tab and the public TeamViewPage so rival
// rosters render in the exact same 6×3 arena grid.

export function sortRosterSlots(rawSlots) {
  return [...(rawSlots || [])].sort((a, b) => {
    const catA = CATEGORY_ORDER.indexOf(a.entity_type);
    const catB = CATEGORY_ORDER.indexOf(b.entity_type);
    if (catA !== catB) return catA - catB;
    const nameA = a.entity?.name || '';
    const nameB = b.entity?.name || '';
    if (nameA !== nameB) return nameA.localeCompare(nameB);
    return (b.entity?.pricing?.price || 0) - (a.entity?.pricing?.price || 0);
  });
}

// FLOW-FILL arena (2026-08-15, mobile parity + testing-agent fix): cells for
// every category (drafted + faded placeholders up to its cap) are packed as a
// CONTINUOUS sequence into columns of STAGE_GRID_ROWS. This means a custom
// shape like {country_leader:1, ceo:17} fits the 6×3 grid exactly (the old
// per-category-column chunking needed 7 columns and clamped the overflow onto
// column 5, overlapping slots). A column is labeled with the FIRST category
// that makes its debut in that column, so every category gets exactly one
// header. `slotConfig` is the league's custom shape ({category: count}) or
// empty for the 3-per-cat default (which reproduces the classic 6×3 board).
export function buildArenaLayout(sortedSlots, slotConfig = {}) {
  const byCat = new Map();
  for (const slot of sortedSlots) {
    const cat = slot.entity_type;
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(slot);
  }

  const hasCustomShape = Object.keys(slotConfig).length > 0;
  const cells = [];
  for (const cat of CATEGORY_ORDER) {
    const group = byCat.get(cat) || [];
    const capRaw = hasCustomShape ? (slotConfig[cat] || 0) : 3;
    // Never hide a drafted person even if the config was later shrunk.
    const target = Math.max(Math.min(capRaw, STAGE_GRID_ROWS * STAGE_GRID_COLS), group.length);
    if (target === 0) continue;
    for (let i = 0; i < target; i += 1) {
      cells.push(group[i] || {
        _isPlaceholder: true,
        entity_id: `__placeholder_${cat}_${i}`,
        entity_type: cat,
        entity: null,
      });
    }
  }

  const columns = [];
  for (let i = 0; i < cells.length; i += STAGE_GRID_ROWS) {
    columns.push(cells.slice(i, i + STAGE_GRID_ROWS));
  }

  const colOffset = Math.max(0, Math.floor((STAGE_GRID_COLS - columns.length) / 2));

  const finalSlots = [];
  const plan = new Array(STAGE_GRID_COLS).fill(null);
  const labeled = new Set();
  columns.forEach((column, idxLocal) => {
    const colIdx = Math.min(idxLocal + colOffset, STAGE_GRID_COLS - 1);
    const firstNew = column.find((s) => !labeled.has(s.entity_type));
    const label = firstNew ? firstNew.entity_type : null;
    if (label) labeled.add(label);
    plan[colIdx] = { label };
    column.forEach((slot, rowIdx) => {
      finalSlots.push({ ...slot, _col: colIdx, _row: rowIdx });
    });
  });

  return { slotsWithGrid: finalSlots, columnPlan: plan };
}

// 2026-09-08 (owner) — a booster runs 7 DAYS FROM ACTIVATION (not the
// gameweek). Shared formatting so every surface says the same thing.
export const BOOSTER_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
export function boosterEndsAt(activatedAtIso, expiresAtIso) {
  if (expiresAtIso) return new Date(expiresAtIso);
  if (!activatedAtIso) return null;
  const t = new Date(activatedAtIso).getTime();
  return Number.isFinite(t) ? new Date(t + BOOSTER_DURATION_MS) : null;
}
export function boosterTimeLeft(endsAt, now = Date.now()) {
  if (!endsAt) return null;
  const ms = endsAt.getTime() - now;
  if (ms <= 0) return 'expired';
  const hrs = Math.floor(ms / (60 * 60 * 1000));
  if (hrs >= 24) return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
  if (hrs >= 1) return `${hrs}h`;
  return `${Math.max(1, Math.floor(ms / 60000))}m`;
}
export function formatBoosterEnd(endsAt) {
  if (!endsAt) return '';
  try {
    return endsAt.toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch (e) {
    return endsAt.toISOString();
  }
}
